import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmt, fmtDay, fmtDateTime, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Minus, Plus, Package, CalendarClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function PileStepper({ label, pileSize, value, onChange, testid }) {
  const v = Number(value) || 0;
  const set = (n) => onChange(Math.max(0, Math.round(n)));
  return (
    <div className="rounded-xl border-2 border-blue-200 bg-white p-3 min-w-0" data-testid={`${testid}-row`}>
      <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
        <span className="font-display font-bold text-base text-slate-800">{label}</span>
        <span className="text-xs font-data text-slate-500">Unité&nbsp;: piles</span>
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <button type="button" onClick={() => set(v - 1)}
          className="tactile h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-xl bg-red-100 text-red-800 flex items-center justify-center active:bg-red-200"
          data-testid={`${testid}-decrease`} aria-label="Diminuer">
          <Minus className="h-7 w-7" strokeWidth={3} />
        </button>
        <input type="number" inputMode="numeric" value={v === 0 ? "" : v} placeholder="0"
          onChange={(e) => set(parseInt(e.target.value || "0", 10))}
          className="h-12 sm:h-14 w-0 flex-1 min-w-0 rounded-xl border-2 border-blue-200 bg-slate-50 text-center font-data text-2xl sm:text-3xl font-black text-blue-900 focus:outline-none focus:ring-4 focus:ring-blue-200"
          data-testid={`${testid}-value`} />
        <button type="button" onClick={() => set(v + 1)}
          className="tactile h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-xl bg-green-100 text-green-800 flex items-center justify-center active:bg-green-200"
          data-testid={`${testid}-increase`} aria-label="Augmenter">
          <Plus className="h-7 w-7" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

export default function StockDeclarationDialog({ open, onOpenChange, onDone, fermeName }) {
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null); // {created_at}

  const { data: types = [] } = useQuery({
    queryKey: ["packaging-types"],
    queryFn: async () => (await api.get("/packaging-types")).data,
    enabled: open,
  });
  const { data: status } = useQuery({
    queryKey: ["decl-status", open],
    queryFn: async () => (await api.get("/stock-declarations/status")).data,
    enabled: open,
  });

  useEffect(() => {
    if (open) { setQty({}); setDone(null); }
  }, [open]);

  const mine = (status?.fermes || [])[0];
  const friday = mine?.expected_friday || status?.friday;
  const notDeclared = mine && !mine.declared;
  const fridayLabel = useMemo(() => {
    if (!friday) return "";
    try {
      return fmtDay(friday);
    } catch { return friday; }
  }, [friday]);

  const submit = async () => {
    const lines = types.map((t) => ({ packaging_type: t.code, piles: Number(qty[t.code]) || 0 }));
    if (lines.reduce((s, l) => s + l.piles, 0) <= 0) { toast.error("Saisissez au moins une quantité supérieure à 0."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/stock-declarations", { lines });
      setDone(data);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const doneWhen = useMemo(() => {
    if (!done?.created_at) return "";
    try {
      return fmtDateTime(done.created_at);
    } catch { return done.created_at; }
  }, [done]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-lg max-h-[92vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="stock-declaration-dialog">
        {done ? (
          <div className="py-6 text-center space-y-3" data-testid="declaration-success">
            <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
            <h3 className="font-display font-extrabold text-xl text-slate-900">Déclaration de stock enregistrée</h3>
            <p className="text-sm text-slate-600">Enregistrée le <strong>{doneWhen}</strong></p>
            <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-3 text-sm text-left space-y-1 max-w-xs mx-auto">
              {(done.lines || []).map((l) => {
                const t = types.find((x) => x.code === l.packaging_type);
                return (
                  <div key={l.packaging_type} className="flex justify-between font-data">
                    <span className="text-slate-600">{t?.label || l.packaging_type}</span>
                    <span className="font-bold text-slate-900">{fmt(l.piles)} piles</span>
                  </div>
                );
              })}
            </div>
            <Button className="h-12 w-full bg-blue-800 hover:bg-blue-900 text-white font-bold" onClick={() => onOpenChange(false)} data-testid="declaration-close-button">
              Fermer
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-700" />
                Déclaration de stock{fermeName ? ` — ${fermeName}` : ""}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-1.5 text-blue-800">
                <CalendarClock className="h-4 w-4" />
                <span className="capitalize">{fridayLabel || "Vendredi"}</span>
              </DialogDescription>
            </DialogHeader>

            {notDeclared && (
              <div className="rounded-lg bg-red-50 border-2 border-red-300 p-3 text-sm text-red-800" data-testid="declaration-warning">
                <p className="font-display font-bold flex items-center gap-1.5">⚠️ {mine?.late ? "DÉCLARATION DE STOCK EN RETARD" : "DÉCLARATION DE STOCK À FAIRE"}</p>
                <p className="mt-0.5">La dernière déclaration attendue était celle du <strong className="capitalize">{fridayLabel}</strong>.</p>
              </div>
            )}

            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="friday-reminder">
              📅 La déclaration de stock est attendue <strong>chaque vendredi en fin de production</strong>. Saisissez vos quantités <strong>en piles</strong>.
              {mine && mine.declared && (
                <span className="block mt-1 text-green-700 font-semibold">✅ Déclaration à jour pour le {fridayLabel}. Une nouvelle validation la remplacera.</span>
              )}
            </div>

            <div className="space-y-3 py-1">
              {types.map((t) => (
                <PileStepper key={t.code} label={t.label} pileSize={t.pile_size || 1}
                  value={qty[t.code] || 0}
                  onChange={(n) => setQty((p) => ({ ...p, [t.code]: n }))}
                  testid={`decl-${t.code}`} />
              ))}
            </div>

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="h-12 w-full sm:w-auto">Annuler</Button>
              <Button onClick={submit} disabled={saving} className="h-12 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white font-bold" data-testid="submit-declaration-button">
                {saving ? "Enregistrement…" : "Valider la déclaration"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
